// bluaIQ — Account Blocked Screen
// Shown after 2 failed payment attempts
// Options: A) Pay subscription to reactivate, B) Pay $5 for archive/read-only, C) Download data
// Data retention: 90 days to decide, then data is deleted

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle, CreditCard, Archive, Download, Clock,
  Check, X, ChevronRight, Shield, HelpCircle, Eye
} from 'lucide-react'

// ── TYPES ─────────────────────────────────────────

interface BlockedAccountState {
  failedAttempts: number
  lastFailedDate: string
  plan: string
  monthlyAmount: number
  dataRetentionDeadline: string // 90 days from block date
  projectCount: number
  storageUsedGB: number
}

// ── DEMO STATE ────────────────────────────────────

const DEMO_BLOCKED: BlockedAccountState = {
  failedAttempts: 2,
  lastFailedDate: '2026-03-20',
  plan: 'PRO',
  monthlyAmount: 89,
  dataRetentionDeadline: '2026-06-20',
  projectCount: 3,
  storageUsedGB: 4.2,
}

// ── COMPONENT ─────────────────────────────────────

export default function AccountBlockedScreen() {
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const [selectedOption, setSelectedOption] = useState<'reactivate' | 'archive' | null>(null)
  const [showDownloadModal, setShowDownloadModal] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const state = DEMO_BLOCKED
  const daysLeft = Math.ceil(
    (new Date(state.dataRetentionDeadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  )

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-5">
      <div className="max-w-xl w-full space-y-5">

        {/* ── Warning header ── */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center justify-center w-[64px] h-[64px] rounded-2xl bg-red-50 mx-auto">
            <AlertTriangle size={32} className="text-red-500" />
          </div>
          <h1 className="text-[26px] font-[900] tracking-[-1px] text-[#1C1A17]">
            {isEs ? 'Cuenta Suspendida' : 'Account Suspended'}
          </h1>
          <p className="text-[15px] font-[600] text-[#9E9890] max-w-sm mx-auto leading-relaxed">
            {isEs
              ? `Tu pago fue rechazado 2 veces. Tu plan ${state.plan} ($${state.monthlyAmount}/mes) necesita un método de pago válido para continuar.`
              : `Your payment failed 2 times. Your ${state.plan} plan ($${state.monthlyAmount}/mo) needs a valid payment method to continue.`
            }
          </p>
        </div>

        {/* ── Data retention countdown ── */}
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3.5">
          <Clock size={18} className="text-amber-600 flex-shrink-0" />
          <div>
            <div className="text-[11px] font-[800] text-amber-700">
              {isEs ? `${daysLeft} días para decidir` : `${daysLeft} days to decide`}
            </div>
            <div className="text-[10px] font-[600] text-amber-600 mt-0.5">
              {isEs
                ? `Si no eliges una opción antes del ${new Date(state.dataRetentionDeadline).toLocaleDateString('es-MX', { month: 'long', day: 'numeric', year: 'numeric' })}, tus datos serán eliminados permanentemente.`
                : `If you don't choose an option before ${new Date(state.dataRetentionDeadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}, your data will be permanently deleted.`
              }
            </div>
          </div>
        </div>

        {/* ── Option A: Reactivate ── */}
        <button
          onClick={() => setSelectedOption('reactivate')}
          className={`w-full text-left border-2 rounded-xl p-5 transition-all duration-200 ${
            selectedOption === 'reactivate'
              ? 'border-[#06539B] bg-[#06539B]/5 shadow-lg shadow-[#06539B]/10'
              : 'border-[#ECEAE4] bg-white hover:border-[#06539B]/40'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`w-[44px] h-[44px] rounded-xl flex items-center justify-center flex-shrink-0 ${
              selectedOption === 'reactivate' ? 'bg-[#06539B] text-white' : 'bg-[#06539B]/10 text-[#06539B]'
            }`}>
              <CreditCard size={22} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-[15px] font-[900] text-[#1C1A17]">
                  {isEs ? 'Reactivar mi plan' : 'Reactivate my plan'}
                </h3>
                <span className="text-[9px] font-[800] bg-[#06539B] text-white px-2 py-0.5 rounded-full uppercase tracking-[0.5px]">
                  {isEs ? 'Recomendado' : 'Recommended'}
                </span>
              </div>
              <p className="text-[12px] font-[600] text-[#9E9890] leading-relaxed">
                {isEs
                  ? `Paga $${state.monthlyAmount} para reactivar tu plan ${state.plan}. Acceso completo a todos tus proyectos y funciones.`
                  : `Pay $${state.monthlyAmount} to reactivate your ${state.plan} plan. Full access to all your projects and features.`
                }
              </p>
              <div className="flex items-center gap-3 mt-3">
                <span className="text-[20px] font-[900] text-[#06539B]">${state.monthlyAmount}<span className="text-[11px] font-[600] text-[#9E9890]">/mo</span></span>
                <div className="flex items-center gap-1.5 text-[10px] font-[700] text-[#22C55E]">
                  <Check size={12} /> {isEs ? 'Todo incluido' : 'Everything included'}
                </div>
              </div>
            </div>
            <ChevronRight size={18} className="text-[#9E9890] mt-1 flex-shrink-0" />
          </div>
        </button>

        {/* ── Option B: Archive mode ── */}
        <button
          onClick={() => setSelectedOption('archive')}
          className={`w-full text-left border-2 rounded-xl p-5 transition-all duration-200 ${
            selectedOption === 'archive'
              ? 'border-[#6B7280] bg-gray-50 shadow-lg shadow-gray-200'
              : 'border-[#ECEAE4] bg-white hover:border-gray-400'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`w-[44px] h-[44px] rounded-xl flex items-center justify-center flex-shrink-0 ${
              selectedOption === 'archive' ? 'bg-[#6B7280] text-white' : 'bg-gray-100 text-[#6B7280]'
            }`}>
              <Eye size={22} />
            </div>
            <div className="flex-1">
              <h3 className="text-[15px] font-[900] text-[#1C1A17] mb-1">
                {isEs ? 'Modo archivo (solo lectura)' : 'Archive mode (read-only)'}
              </h3>
              <p className="text-[12px] font-[600] text-[#9E9890] leading-relaxed">
                {isEs
                  ? 'Ve toda tu información pero no podrás editar, crear ni usar funciones de IA. Tu data permanece segura.'
                  : 'View all your data but you cannot edit, create, or use AI features. Your data stays safe.'
                }
              </p>
              <div className="flex items-center gap-3 mt-3">
                <span className="text-[20px] font-[900] text-[#6B7280]">$5<span className="text-[11px] font-[600] text-[#9E9890]">/mo</span></span>
                <div className="flex flex-wrap gap-2 text-[9px] font-[700]">
                  <span className="flex items-center gap-1 text-[#22C55E]"><Check size={10} /> {isEs ? 'Ver datos' : 'View data'}</span>
                  <span className="flex items-center gap-1 text-red-400"><X size={10} /> {isEs ? 'Sin edición' : 'No editing'}</span>
                  <span className="flex items-center gap-1 text-red-400"><X size={10} /> {isEs ? 'Sin IA' : 'No AI'}</span>
                </div>
              </div>
            </div>
            <ChevronRight size={18} className="text-[#9E9890] mt-1 flex-shrink-0" />
          </div>
        </button>

        {/* ── Continue button ── */}
        {selectedOption && (
          <button className={`w-full text-[14px] font-[800] py-4 rounded-full text-white transition-all duration-300 shadow-lg ${
            selectedOption === 'reactivate'
              ? 'bg-[#06539B] hover:bg-[#14457E] shadow-[#06539B]/25'
              : 'bg-[#6B7280] hover:bg-[#4B5563] shadow-gray-300'
          }`}>
            {selectedOption === 'reactivate'
              ? (isEs ? `Pagar $${state.monthlyAmount} y reactivar` : `Pay $${state.monthlyAmount} & reactivate`)
              : (isEs ? 'Activar modo archivo — $5/mes' : 'Activate archive mode — $5/mo')
            }
          </button>
        )}

        {/* ── Download data option ── */}
        <div className="border-t border-[#ECEAE4] pt-4">
          <button
            onClick={() => setShowDownloadModal(true)}
            className="w-full flex items-center justify-center gap-2.5 text-[12px] font-[800] text-[#9E9890] hover:text-[#1C1A17] py-3 border border-[#ECEAE4] rounded-full hover:border-[#06539B] transition-colors"
          >
            <Download size={16} />
            {isEs ? 'Descargar toda mi información' : 'Download all my data'}
          </button>
          <p className="text-center text-[10px] font-[600] text-[#9E9890] mt-2">
            {isEs
              ? `${state.projectCount} proyectos · ${state.storageUsedGB} GB de datos`
              : `${state.projectCount} projects · ${state.storageUsedGB} GB of data`
            }
          </p>
        </div>

        {/* ── Help ── */}
        <div className="bg-[#F2F0EB] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <HelpCircle size={14} className="text-[#9E9890]" />
            <h4 className="text-[11px] font-[800] text-[#1C1A17]">
              {isEs ? '¿Necesitas ayuda?' : 'Need help?'}
            </h4>
          </div>
          <p className="text-[10px] font-[600] text-[#9E9890] leading-relaxed">
            {isEs
              ? 'Si crees que esto es un error, contacta a soporte: support@natamiapp.com. Si tu tarjeta fue robada o tienes problemas con tu banco, podemos extender el período de gracia.'
              : "If you think this is an error, contact support: support@natamiapp.com. If your card was stolen or you're having bank issues, we can extend the grace period."
            }
          </p>
        </div>

        {/* ── Account info footer ── */}
        <div className="flex items-center justify-center gap-4 text-[9px] font-[700] text-[#9E9890]">
          <span className="flex items-center gap-1"><Shield size={10} /> {isEs ? 'Datos cifrados' : 'Data encrypted'}</span>
          <span>·</span>
          <span>{isEs ? 'Último intento' : 'Last attempt'}: {new Date(state.lastFailedDate).toLocaleDateString(isEs ? 'es-MX' : 'en-US')}</span>
        </div>
      </div>

      {/* ── Download Modal ── */}
      {showDownloadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-5">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-[16px] font-[900] text-[#1C1A17]">
                {isEs ? 'Descargar datos' : 'Download data'}
              </h3>
              <button onClick={() => setShowDownloadModal(false)} className="p-1 hover:bg-[#F2F0EB] rounded-lg">
                <X size={18} />
              </button>
            </div>

            <p className="text-[12px] font-[600] text-[#9E9890] leading-relaxed">
              {isEs
                ? 'Se generará un archivo ZIP con toda tu información:'
                : "We'll generate a ZIP file with all your data:"
              }
            </p>

            <div className="space-y-2">
              {[
                { icon: '📁', label: isEs ? 'Proyectos y presupuestos' : 'Projects & budgets', format: 'CSV + JSON' },
                { icon: '📄', label: isEs ? 'Contratos y documentos' : 'Contracts & documents', format: 'PDF' },
                { icon: '📸', label: isEs ? 'Fotos de avance' : 'Progress photos', format: 'JPG/PNG' },
                { icon: '🧾', label: isEs ? 'Recibos y gastos' : 'Receipts & expenses', format: 'CSV + images' },
                { icon: '📧', label: isEs ? 'Emails generados' : 'Generated emails', format: 'TXT' },
                { icon: '👤', label: isEs ? 'Contactos y proveedores' : 'Contacts & suppliers', format: 'CSV' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-[#F2F0EB] last:border-0">
                  <span className="text-[16px]">{item.icon}</span>
                  <span className="text-[12px] font-[700] text-[#1C1A17] flex-1">{item.label}</span>
                  <span className="text-[9px] font-[700] text-[#9E9890] bg-[#F2F0EB] px-2 py-0.5 rounded-full">{item.format}</span>
                </div>
              ))}
            </div>

            <div className="text-[10px] font-[600] text-[#9E9890] bg-[#F2F0EB] rounded-lg p-3">
              {isEs
                ? `Tamaño estimado: ~${(state.storageUsedGB * 0.8).toFixed(1)} GB. La descarga puede tomar algunos minutos.`
                : `Estimated size: ~${(state.storageUsedGB * 0.8).toFixed(1)} GB. Download may take a few minutes.`
              }
            </div>

            <button
              onClick={() => { setDownloading(true); setTimeout(() => setDownloading(false), 3000) }}
              disabled={downloading}
              className="w-full flex items-center justify-center gap-2 bg-[#06539B] text-white text-[13px] font-[800] py-3.5 rounded-full disabled:opacity-50 transition-colors"
            >
              <Download size={16} />
              {downloading
                ? (isEs ? 'Preparando descarga...' : 'Preparing download...')
                : (isEs ? 'Descargar todo (.zip)' : 'Download all (.zip)')
              }
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
